import React from 'react';
import {
  Building,
  User,
  Phone,
  Mail,
  MapPin,
  Briefcase,
  Calendar,
  Clock,
  FileText,
  CheckSquare,
  IndianRupee,
  PhoneCall,
  Send,
  Edit,
  Eye,
  TrendingUp
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { LeadWithMetrics } from '@/types/dashboard';
import { RiskBadge } from './leads/RiskBadge';

interface EnhancedLeadCardProps {
  lead: LeadWithMetrics;
  onViewDetails?: (lead: LeadWithMetrics) => void;
  onEdit?: (lead: LeadWithMetrics) => void;
  onCall?: (lead: LeadWithMetrics) => void;
  onEmail?: (lead: LeadWithMetrics) => void;
}

export const EnhancedLeadCard: React.FC<EnhancedLeadCardProps> = ({
  lead,
  onViewDetails,
  onEdit,
  onCall,
  onEmail
}) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'HOT':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'WARM':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'COLD':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'NEW':
        return 'bg-blue-100 text-blue-800';
      case 'CONTACTED':
        return 'bg-yellow-100 text-yellow-800';
      case 'DISCUSSION':
        return 'bg-purple-100 text-purple-800';
      case 'PROPOSAL':
        return 'bg-orange-100 text-orange-800';
      case 'NEGOTIATION':
        return 'bg-indigo-100 text-indigo-800';
      case 'WON':
        return 'bg-green-100 text-green-800';
      case 'LOST':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Card className="hover:shadow-lg transition-all duration-200 border border-gray-200 overflow-hidden">
      <div className="p-5 space-y-4">
        {/* Header Section */}
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 truncate mb-1">
              {lead.name}
            </h3>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Building className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="truncate">{lead.company}</span>
            </div>
          </div>
          <div className="flex flex-col gap-1.5 ml-3">
            <Badge variant="outline" className={getPriorityColor(lead.priority)}>
              {lead.priority}
            </Badge>
            <Badge variant="secondary" className={`${getStageColor(lead.stage)} text-xs`}>
              {lead.stage}
            </Badge>
          </div>
        </div>

        {/* Contact Information */}
        <div className="space-y-2">
          {lead.contactPerson && (
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <User className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
              <span className="truncate">{lead.contactPerson}</span>
            </div>
          )}

          {lead.phoneNumber && (
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Phone className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
              <span className="truncate">{lead.phoneNumber}</span>
            </div>
          )}

          {lead.email && (
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Mail className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
              <span className="truncate">{lead.email}</span>
            </div>
          )}
        </div>

        {/* Metadata Section */}
        <div className="grid grid-cols-2 gap-2 text-xs">
          {lead.industry && (
            <div className="flex items-center gap-1.5 text-gray-600">
              <Briefcase className="h-3.5 w-3.5 text-gray-400" />
              <span className="truncate">{lead.industry}</span>
            </div>
          )}

          {lead.location && (
            <div className="flex items-center gap-1.5 text-gray-600">
              <MapPin className="h-3.5 w-3.5 text-gray-400" />
              <span className="truncate">{lead.location}</span>
            </div>
          )}
        </div>

        {/* Time Metrics */}
        <div className="grid grid-cols-2 gap-3 py-3 border-t border-b border-gray-100">
          <div>
            <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
              <TrendingUp className="h-3 w-3" />
              <span>In Pipeline</span>
            </div>
            <p className="text-sm font-semibold text-gray-900">{lead.daysInPipeline} days</p>
          </div>

          <div>
            <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
              <Clock className="h-3 w-3" />
              <span>In Stage</span>
            </div>
            <p className="text-sm font-semibold text-gray-900">{lead.daysInStage} days</p>
          </div>

          <div>
            <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
              <Calendar className="h-3 w-3" />
              <span>Last Contact</span>
            </div>
            <p className="text-xs font-medium text-gray-900">{formatDate(lead.lastContactDate || lead.lastActivity || undefined)}</p>
          </div>

          <div>
            <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
              <Calendar className="h-3 w-3" />
              <span>Next Follow-up</span>
            </div>
            <p className="text-xs font-medium text-gray-900">{formatDate(lead.nextFollowUpDate)}</p>
          </div>
        </div>

        {/* Activity Metrics */}
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1.5 text-gray-600">
            <CheckSquare className="h-3.5 w-3.5 text-gray-400" />
            <span className="font-medium">{lead.taskCount || lead._count?.tasks || 0}</span>
            <span>tasks</span>
          </div>

          <div className="flex items-center gap-1.5 text-gray-600">
            <FileText className="h-3.5 w-3.5 text-gray-400" />
            <span className="font-medium">{lead.noteCount || lead._count?.notes || 0}</span>
            <span>notes</span>
          </div>
        </div>

        {/* Risk Indicators */}
        {lead.riskIndicators && lead.riskIndicators.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {lead.riskIndicators.slice(0, 2).map((risk, index) => (
              <RiskBadge
                key={index}
                type={risk.type}
                severity={risk.severity}
              />
            ))}
            {lead.riskIndicators.length > 2 && (
              <Badge variant="outline" className="bg-gray-100 text-gray-600 text-xs">
                +{lead.riskIndicators.length - 2} more
              </Badge>
            )}
          </div>
        )}

        {/* Deal Information */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <IndianRupee className="h-4 w-4 text-blue-600" />
              <span className="text-xs text-gray-600">Deal Value</span>
            </div>
            <p className="text-lg font-bold text-blue-900">
              {formatCurrency(lead.dealValue || lead.value || 0)}
            </p>
          </div>

          {lead.expectedClose && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-600">Expected Close</span>
              <span className="font-medium text-gray-900">{formatDate(lead.expectedClose)}</span>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
            onClick={() => onCall?.(lead)}
          >
            <PhoneCall className="h-3.5 w-3.5" />
            Call
          </Button>

          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
            onClick={() => onEmail?.(lead)}
          >
            <Send className="h-3.5 w-3.5" />
            Email
          </Button>

          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
            onClick={() => onEdit?.(lead)}
          >
            <Edit className="h-3.5 w-3.5" />
            Edit
          </Button>

          <Button
            variant="default"
            size="sm"
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
            onClick={() => onViewDetails?.(lead)}
          >
            <Eye className="h-3.5 w-3.5" />
            View
          </Button>
        </div>
      </div>
    </Card>
  );
};
